import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  RESPIRATORY_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Levante Asthma Study (internal reference)
 * 
 * Inclusion criteria:
 * - Age >=18
 * - Asthma diagnosis >12 months ago
 * - On med/high dose ICS + LABA (+/- LAMA)
 * - Have asthma symptoms in past week
 * - At least 1 exacerbation in past 12 months
 * - BMI 18-37
 */
export const levanteAsthmaScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "asthma_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with asthma?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I have been diagnosed with asthma by a healthcare provider " },
      { 
        value: "no", 
        label: "No, I have not been diagnosed with asthma by a healthcare provider",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for people who have been diagnosed with asthma.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: false }],
    }),
  },
  {
    id: "asthma_diagnosis_timing",
    type: QuestionType.RADIO,
    question: "When was your asthma diagnosed?",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "within_12_months", 
        label: "I was diagnosed within the past 12 months",
        disqualify: true,
      },
      { value: "more_than_12_months", label: "I was diagnosed more than 12 months ago" },
    ],
    disqualifyMessage:
      "This study requires participants to have had asthma for more than 12 months.",
    attributeMapping: mappers.value({
      within_12_months: [{ attributeDefinitionId: 23808, booleanAttributeValue: false }], // has_asthma_diagnosis_12_months
      more_than_12_months: [{ attributeDefinitionId: 23808, booleanAttributeValue: true }],
    }),
  },
  {
    id: "asthma_medications_detailed",
    type: QuestionType.RADIO,
    question: "In terms of asthma medications, select which option best describes what you use:",
    helpText: "If you're unsure about your medication type, check the name on your inhaler or ask your pharmacist",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "no_inhaled", 
        label: "I don't use any inhalers at the moment",
        disqualify: true,
      },
      { 
        value: "reliever_only", 
        label: "I use a rescue/reliever inhaler (such as Ventolin, ProAir, or Proventil) when I need it",
        disqualify: true,
      },
      { 
        value: "ics_only", 
        label: "I use a daily controller that contains only an inhaled corticosteroid (such as Flovent, Pulmicort, QVAR or Arnuity)",
        disqualify: true,
      },
      { 
        value: "ics_laba", 
        label: "I use a daily combination inhaler (such as Advair, Symbicort, Dulera, Breo, Breztri or Trelegy Ellipta)",
      },
      { 
        value: "none_applies", 
        label: "None of these options apply to me",
        disqualify: true,
      },
      { value: "unsure", label: "I'm unsure about my asthma medications" },
    ],
    disqualifyMessage:
      "This study requires participants to be on a daily combination inhaler for asthma.",
    // TODO: Add attribute mapping when medication type attributes are available
    attributeMapping: mappers.value({
      no_inhaled: [],
      reliever_only: [],
      ics_only: [],
      ics_laba: [],
      none_applies: [],
      unsure: [],
    }),
  },
  {
    id: "asthma_symptoms_past_week",
    type: QuestionType.RADIO,
    question: "Do any of these statements describe your asthma symptoms in the past week?",
    helpText: "You can only click on one option; if more than one are true simply select the first one that applies to you.",
    required: true,
    allowBack: true,
    options: [
      { value: "woken_overnight", label: "I have been woken overnight with symptoms of asthma" },
      { value: "morning_symptoms", label: "I notice my asthma symptoms first thing in the morning" },
      { value: "daily_activities_affected", label: "My daily activities are affected by my asthma symptoms" },
      { value: "shortness_of_breath", label: "I noticed shortness of breath because of my asthma" },
      { value: "wheezing", label: "I wheezed a lot because of my asthma" },
      { value: "reliever_most_days", label: "I needed to use my rescue/reliever inhaler (such as Ventolin) most days" },
      { 
        value: "none", 
        label: "None of these apply to me in the past week",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants who are experiencing asthma symptoms despite the use of their daily controller inhaler.",
    // TODO: Add specific symptom attributes when available
    attributeMapping: mappers.value({
      woken_overnight: [],
      morning_symptoms: [],
      daily_activities_affected: [],
      shortness_of_breath: [],
      wheezing: [],
      reliever_most_days: [],
      none: [],
    }),
  },
  {
    id: "asthma_exacerbations",
    type: QuestionType.RADIO,
    question: "With regards to your asthma, which of these statements applies to you?",
    required: true,
    allowBack: true,
    options: [
      { value: "hospital_12_months", label: "I have had at least 1 flare-up requiring hospital treatment in the past 12 months" },
      { value: "steroids_12_months", label: "I have had at least 1 flare-up requiring steroid tablets or injection in the past 12 months" },
      { 
        value: "stable_12_months", 
        label: "My asthma has been stable for 12 months, requiring only my usual puffers",
        disqualify: true,
      },
      { 
        value: "life_threatening_ever", 
        label: "I have had a life threatening asthma attack in the past",
        disqualify: true,
      },
      { 
        value: "none", 
        label: "None of these options apply to me",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study has specific requirements regarding asthma exacerbation history.",
    attributeMapping: mappers.value({
      hospital_12_months: [
        { attributeDefinitionId: 23824, booleanAttributeValue: true }, // has_asthma_exacerbation_severe_12_months
      ],
      steroids_12_months: [
        { attributeDefinitionId: 26442, booleanAttributeValue: true }, // has_asthma_exacerbation_steroids_12_months
      ],
      stable_12_months: [
        { attributeDefinitionId: 23824, booleanAttributeValue: false },
        { attributeDefinitionId: 26442, booleanAttributeValue: false },
      ],
      life_threatening_ever: [
        { attributeDefinitionId: 26399, booleanAttributeValue: true }, // has_asthma_lifethreatening_event_ever
      ],
      none: [],
    }),
  },
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    minBMI: 18,
    maxBMI: 38,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },
  {
    id: "lung_conditions",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "copd",
        label: "I have COPD (Chronic Obstructive Pulmonary Disease)",
        disqualify: true,
      },
      {
        value: "interstitial_lung",
        label: "I have interstitial lung disease or lung fibrosis",
        disqualify: true,
      },
      {
        value: "alpha1",
        label: "I have alpha 1 antitrypsin deficiency",
        disqualify: true,
      },
      {
        value: "lung_cancer",
        label: "I have lung cancer",
        disqualify: true,
      },
      {
        value: "bronchiectasis",
        label: "I have bronchiectasis",
        disqualify: true,
      },
      {
        value: "cystic_fibrosis",
        label: "I have cystic fibrosis",
        disqualify: true,
      },
      { value: "none", label: "None of the above apply to me" },
    ],
    disqualifyMessage:
      "This study is specifically for asthma and cannot include people with other serious lung conditions.",
    attributeMapping: mappers.value({
      copd: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true }],
      interstitial_lung: [{ attributeDefinitionId: 25661, booleanAttributeValue: true }], // has_ILD_prerequisite
      alpha1: [{ attributeDefinitionId: 25637, booleanAttributeValue: true }], // has_a1ad_prerequisite
      lung_cancer: [], // TODO: Add lung cancer attribute when available
      bronchiectasis: [], // TODO: Add bronchiectasis attribute when available
      cystic_fibrosis: [{ attributeDefinitionId: 25767, booleanAttributeValue: true }], // has_CF_prerequisite
      none: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: false },
        { attributeDefinitionId: 25661, booleanAttributeValue: false },
        { attributeDefinitionId: 25637, booleanAttributeValue: false },
        { attributeDefinitionId: 25767, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "serious_conditions",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "end_stage_kidney",
        label: "I have end stage kidney disease or I am currently on dialysis",
        disqualify: true,
      },
      {
        value: "liver_failure",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "active_tb",
        label: "I have active TB or am on treatment for TB",
        disqualify: true,
      },
      {
        value: "hepatitis",
        label: "I have Hepatitis B or C",
        disqualify: true,
      },
      {
        value: "hiv",
        label: "I have HIV",
        disqualify: true,
      },
      {
        value: "cancer_5_years",
        label: "I have had a cancer diagnosis or cancer treatment within the past 5 years (excluding skin cancers such as SCC and BCC and in-situ cervical cancer)",
        disqualify: true,
      },
      { value: "none", label: "None of the above apply to me" },
    ],
    disqualifyMessage:
      "This study cannot include people with these serious medical conditions.",
    attributeMapping: mappers.value({
      end_stage_kidney: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        // TODO: Add dialysis attribute when available
      ],
      liver_failure: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
      ],
      severe_heart_failure: [
        { attributeDefinitionId: 25631, booleanAttributeValue: true }, // has_heartfailure_prerequisite
      ],
      active_tb: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: true },
      ],
      hepatitis: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
      ],
      hiv: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
      ],
      cancer_5_years: [
        { attributeDefinitionId: 26399, booleanAttributeValue: true }, // has_cancer_diagnosis_5_years
      ],
      none: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
        { attributeDefinitionId: 25631, booleanAttributeValue: false },
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false },
        { attributeDefinitionId: 26399, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "additional_medical_conditions",
    type: QuestionType.RADIO,
    question: "Do any of these statements apply to you?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "long_term_oral_steroids",
        label: "I am on long term oral steroid medications (such as prednisone)",
        disqualify: true,
      },
      {
        value: "biologic_treatments",
        label: "I have taken biologic treatments within the past 4 months (these include treatments like Xolair, Nucala, Fasenra, Dupixent)",
        disqualify: true,
      },
      {
        value: "immune_suppressive",
        label: "I have been on immune suppressing medications within the past 3 months",
        disqualify: true,
      },
      {
        value: "donated_blood",
        label: "I have donated blood within the past 3 months",
        disqualify: true,
      },
      {
        value: "major_surgery",
        label: "I have had major surgery within the past 2 months",
        disqualify: true,
      },
      { value: "none", label: "None of the above apply to me" },
    ],
    disqualifyMessage:
      "This study cannot include people with these medical conditions or recent treatments.",
    // TODO: Add attribute mapping when specific attributes are available
    attributeMapping: mappers.value({
      long_term_oral_steroids: [],
      biologic_treatments: [],
      immune_suppressive: [],
      donated_blood: [],
      major_surgery: [],
      none: [],
    }),
  },
  {
    id: "smoking_history",
    type: QuestionType.RADIO,
    question: "Regarding your smoking history, select the option that best reflects your situation:",
    helpText: "Oral cannabis use is permitted",
    required: true,
    allowBack: true,
    options: [
      { value: "never_smoked", label: "I have never smoked cigarettes, cannabis or used vapes on a regular basis" },
      { value: "quit_not_heavy", label: "I quit smoking or vaping more than 6 months ago and was NOT a heavy smoker" },
      {
        value: "quit_heavy",
        label: "I quit smoking or vaping more than 6 months ago and WAS a heavy smoker",
        disqualify: true,
      },
      {
        value: "current_smoker",
        label: "I currently smoke cigarettes, cannabis or use vapes, or have done so regularly within the past 6 months",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study cannot include current smokers or people with a heavy smoking history.",
    // TODO: Add attributes for:
    // - Cannabis use (only has had_cannabis_ever)
    // - Vaping/e-cigarette use (no attributes found)
    // - Heavy smoking definition (no pack-years or heavy smoker boolean found)
    attributeMapping: mappers.value({
      never_smoked: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false }, // is_smoker
        { attributeDefinitionId: 20561, booleanAttributeValue: false }, // is_pastsmoker
      ],
      quit_not_heavy: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true },
        { attributeDefinitionId: 23791, booleanAttributeValue: true }, // quit_smoking_6_months
      ],
      quit_heavy: [
        { attributeDefinitionId: 20917, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true },
        { attributeDefinitionId: 23791, booleanAttributeValue: true },
      ],
      current_smoker: [
        { attributeDefinitionId: 20917, booleanAttributeValue: true },
      ],
    }),
  },
  {
    id: "other_studies",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am in another clinical trial",
        disqualify: true,
      },
      { value: "no", label: "No, I am not in any other clinical trials" },
    ],
    disqualifyMessage:
      "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false }],
    }),
  },
  {
    id: "willing_to_attend",
    type: QuestionType.RADIO,
    question: "Are you willing to attend study visits at a research site?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I am willing to attend in-person and virtual visits for up to 1 year" },
      {
        value: "no",
        label: "No, I cannot commit to this appointment schedule",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss this schedule further with the research team",
      },
    ],
    disqualifyMessage:
      "This study requires attendance at in-person visits.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
      no: [{ attributeDefinitionId: 23479, booleanAttributeValue: false }],
      discuss: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
    }),
  },
  {
    id: "medical_records_consent",
    type: QuestionType.RADIO,
    question: "Are you able to bring along some medical records to the trial site to clarify your medical history?",
    required: true,
    allowBack: true,
    options: [
      { value: "bring_records", label: "Yes, I am able to bring records relating to my asthma" },
      { value: "consent_access", label: "Yes, I can give consent for the trial site to access these records for me" },
      {
        value: "no_share",
        label: "No, I would prefer not to share my medical records",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires access to medical records to verify your asthma history.",
    // TODO: Add medical records consent attribute when available
    attributeMapping: mappers.value({
      bring_records: [],
      consent_access: [],
      no_share: [],
    }),
  },
];